import React, { useState, useEffect } from 'react';
import { ChartDataService } from '../../services/ChartDataService';
import { useUnifiedAuth } from '../../auth/useUnifiedAuth';
import { useSAPDataStore } from '../../store/SAPDataStore';
import { useChartCache } from '../../hooks/useChartCache';
import BaseChart from './BaseChart';

// Backend returns { "2015-01-21 A1": { "1 - 200": 5058000, ... }, ... }
type AverageProductionSpeedResults = Record<string, Record<string, number>>;

interface AverageProductionSpeedChartProps {
  machineSerialNo?: string;
  lineId?: string;
  shifts?: string[];
  className?: string;
}

const AverageProductionSpeedChart: React.FC<AverageProductionSpeedChartProps> = ({ 
  machineSerialNo, 
  shifts = [],
  className = ""
}) => {
  const { token } = useUnifiedAuth();
  const { dateRange } = useSAPDataStore();
  const [data, setData] = useState<AverageProductionSpeedResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use chart cache for better performance
  const chartCache = useChartCache<AverageProductionSpeedResults>({
    maxAge: 5 * 60 * 1000, // 5 minutes
    maxEntries: 30 // Cache up to 30 different chart configurations
  });

  const fetchAverageProductionSpeedData = async () => {
    // Don't fetch if no shifts selected
    if (!shifts || shifts.length === 0) {
      setData(null);
      setLoading(false);
      return;
    }

    if (!machineSerialNo || !dateRange.start || !dateRange.end) {
      setLoading(false);
      return;
    }
    if (!token) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    // Check cache first
    const cachedData = chartCache.getCachedData(
      'pc.average_production_speed',
      machineSerialNo,
      dateRange,
      shifts
    );

    if (cachedData) {
      setData(cachedData);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = ChartDataService.buildRequestParams(machineSerialNo, dateRange, shifts);
      const response = await ChartDataService.fetchAverageProductionSpeed(params, token);

      if (response.success && response.data) {
        const responseData = response.data as any;
        
        console.log('📊 AverageProductionSpeedChart: Raw API response:', {
          success: response.success,
          responseKeys: Object.keys(responseData),
          data: responseData
        });
        
        // Backend returns { status, results, error } - extract results
        const results = responseData.results || responseData;
        
        console.log('📊 AverageProductionSpeedChart: Extracted results:', {
          resultsKeys: results ? Object.keys(results) : 'null',
          hasAverageProductionSpeed: !!results?.average_production_speed
        });
        
        setData(results);
        chartCache.setCachedData('pc.average_production_speed', machineSerialNo, dateRange, shifts, results);
      } else {
        setError(response.error || 'Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching average production speed data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch average production speed data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAverageProductionSpeedData();
  }, [machineSerialNo, dateRange.start, dateRange.end, shifts]);

  // Transform data for BaseChart - Create stacked chart by date+shift
  const chartData = React.useMemo(() => {
    console.log('📊 AverageProductionSpeedChart: chartData useMemo, data:', data);
    
    // Format: { "2015-01-21 A1": { "1 - 200": 5058000, "351 - 400": 22000 }, ... }
    if (!data || typeof data !== 'object') {
      console.log('📊 AverageProductionSpeedChart: No data');
      return { categories: [], series: [] };
    }
    
    // PC.average_production_speed ignores shift_name, so the chart filters the shifts itself:
    // the shift is the text after the last space of the key (all shifts when none are given)
    const isSelectedShift = (key: string) =>
      shifts.length === 0 || shifts.includes(key.substring(key.lastIndexOf(' ') + 1));

    // Get all category keys (e.g., "2015-01-21 A1") - filter out metadata and unselected shifts
    const categoryKeys = Object.keys(data).filter(key => 
      key !== 'status' && key !== 'error' && typeof data[key] === 'object' && isSelectedShift(key)
    );
    
    console.log('📊 AverageProductionSpeedChart: categoryKeys:', categoryKeys.length);
    
    if (categoryKeys.length === 0) return { categories: [], series: [] };

    // Collect all unique speed ranges
    const speedRangeSet = new Set<string>();
    categoryKeys.forEach(key => {
      const ranges = data[key];
      if (ranges && typeof ranges === 'object') {
        Object.keys(ranges).forEach(range => speedRangeSet.add(range));
      }
    });

    // Sort speed ranges numerically (parse first number)
    const speedRanges = Array.from(speedRangeSet).sort((a, b) => {
      const parseRange = (range: string) => {
        const match = range.match(/^(\d+)/);
        return match ? parseInt(match[1]) : 999;
      };
      return parseRange(a) - parseRange(b);
    });

    console.log('📊 AverageProductionSpeedChart: speedRanges:', speedRanges);

    // Sort categories by date then shift
    const sortedCategories = [...categoryKeys].sort((a, b) => {
      // Parse "2015-01-21 A1" format
      const [dateA, shiftA] = a.split(' ');
      const [dateB, shiftB] = b.split(' ');
      
      const dateCompare = dateA.localeCompare(dateB);
      if (dateCompare !== 0) return dateCompare;
      return (shiftA || '').localeCompare(shiftB || '');
    });

    // Build series for each speed range
    const series = speedRanges.map(range => ({
      name: range === '0' ? 'Speed 0' : range,
      data: sortedCategories.map(category => {
        const ranges = data[category];
        const value = ranges?.[range];
        return typeof value === 'number' ? value : (parseFloat(value as string) || 0);
      })
    }));

    // Calculate totals per category (for average calculation)
    const categoryTotals = sortedCategories.map(category => {
      const ranges = data[category];
      if (!ranges || typeof ranges !== 'object') return 0;
      return Object.values(ranges).reduce((sum: number, val) => {
        const numVal = typeof val === 'number' ? val : parseFloat(val as string) || 0;
        return sum + numVal;
      }, 0);
    });

    // Calculate average of totals
    const totalSum = categoryTotals.reduce((sum, val) => sum + val, 0);
    const average = categoryTotals.length > 0 ? totalSum / categoryTotals.length : 0;

    return {
      categories: sortedCategories,
      series,
      average
    };
  }, [data, shifts]);

  // Show loading state in BaseChart when loading (check BEFORE shifts check)
  if (loading) {
    return (
      <div className={`w-full ${className}`}>
        <BaseChart
          type="column"
          height={400}
          categories={[]}
          series={[]}
          loading={true}
        />
      </div>
    );
  }

  if (!shifts || shifts.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h3 className="text-lg font-semibold theme-text-primary mb-2">No Shifts Selected</h3>
          <p className="theme-text-secondary">No shifts selected or available for the selected timeframe</p>
        </div>
      </div>
    );
  }

  // Check for data
  const hasSpeedData = data && Object.keys(data).some(key => 
    key !== 'status' && key !== 'error' && typeof data[key] === 'object'
  );
  
  if (!hasSpeedData || chartData.categories.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h3 className="text-lg font-semibold theme-text-primary mb-2">No Production Speed Data</h3>
          <p className="theme-text-secondary">No average production speed data found for the selected period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      <BaseChart
        commentProps={{ chartType: 'average_production_speed', machineBK: machineSerialNo }}
        type="column"
        height={Math.max(400, 500)}
        categories={chartData.categories}
        series={chartData.series}
        yAxis={{
          title: 'Time at Speed (HH:MM:SS)',
          min: 0
        }}
        customOptions={{
          xAxis: {
            labels: {
              rotation: -45,
              style: {
                fontSize: '11px'
              }
            }
          },
          yAxis: {
            labels: {
              formatter: function(this: any) {
                const milliseconds = this.value;
                
                // Convert milliseconds to hours:minutes:seconds.milliseconds
                const totalSeconds = Math.floor(milliseconds / 1000);
                const hours = Math.floor(totalSeconds / 3600);
                const minutes = Math.floor((totalSeconds % 3600) / 60);
                const seconds = totalSeconds % 60;
                const remainingMs = Math.round(milliseconds % 1000);
                
                // Format as HH:MM:SS.mmm
                const hoursStr = hours.toString().padStart(2, '0');
                const minutesStr = minutes.toString().padStart(2, '0');
                const secondsStr = seconds.toString().padStart(2, '0');
                const msStr = remainingMs.toString().padStart(3, '0');
                
                // For chart labels, show shorter format for better readability
                if (hours > 0) {
                  return `${hoursStr}:${minutesStr}:${secondsStr}`;
                } else if (minutes > 0) {
                  return `${minutesStr}:${secondsStr}`;
                } else {
                  return `${secondsStr}.${msStr}s`;
                }
              }
            },
            plotLines: (chartData.average ?? 0) > 0 ? [{
              color: '#EF4444',
              width: 2,
              value: chartData.average,
              dashStyle: 'Dash' as const,
              zIndex: 5,
              label: {
                text: (() => {
                  const totalSeconds = Math.floor((chartData.average ?? 0) / 1000);
                  const hours = Math.floor(totalSeconds / 3600);
                  const minutes = Math.floor((totalSeconds % 3600) / 60);
                  const seconds = totalSeconds % 60;
                  if (hours > 0) {
                    return `Avg: ${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                  } else if (minutes > 0) {
                    return `Avg: ${minutes}:${seconds.toString().padStart(2, '0')}`;
                  } else {
                    return `Avg: ${seconds}s`;
                  }
                })(),
                align: 'right' as const,
                style: {
                  color: '#EF4444',
                  fontWeight: 'bold'
                }
              }
            }] : []
          },
          plotOptions: {
            column: {
              stacking: 'normal' as const,
              dataLabels: {
                enabled: false
              }
            }
          },
          tooltip: {
            formatter: function(this: any) {
              const point = this.point;
              const milliseconds = point.y || 0;
              
              // Get category from point.category or from xAxis categories
              const category = (point as any).category || 
                               (this.series.xAxis?.categories && this.series.xAxis.categories[point.x as number]) ||
                               String(this.x);
              
              // Parse date and shift from category (e.g., "2023-01-12 A1")
              const categoryStr = String(category);
              const parts = categoryStr.split(' ') || [];
              const date = parts[0] || '';
              const shift = parts[1] || '';
              
              // Format time as HH:MM:SS
              const totalSeconds = Math.floor(milliseconds / 1000);
              const hours = Math.floor(totalSeconds / 3600);
              const minutes = Math.floor((totalSeconds % 3600) / 60);
              const seconds = totalSeconds % 60;
              
              const hoursStr = hours.toString().padStart(2, '0');
              const minutesStr = minutes.toString().padStart(2, '0');
              const secondsStr = seconds.toString().padStart(2, '0');
              const timeStr = `${hoursStr}:${minutesStr}:${secondsStr} h`;
              
              return `<b>Speed Range:</b> ${this.series.name}<br/>` +
                     `<b>Duration:</b> ${timeStr}<br/>` +
                     `<b>Shift:</b> ${shift}<br/>` +
                     `<b>Date:</b> ${date}`;
            }
          }
        }}
        legend={{
          enabled: true,
          position: 'right'
        }}
        loading={loading}
        error={error}
        onRetry={fetchAverageProductionSpeedData}
        className="w-full"
      />
    </div>
  );
};

export default AverageProductionSpeedChart;